import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../../api/client";
import PortalSidebar from "../../components/portal/PortalSidebar";
import { 
  ArrowLeft, User, Calendar, FileText, DollarSign, 
  Building2, Briefcase, ClipboardList, Edit2, Save, X,
  AlertCircle, CheckCircle, Clock, Stethoscope, Shield
} from "lucide-react";
import { toast } from "sonner";

function getStatusColor(status) {
  const colors = {
    'ACTIVE': 'bg-emerald-100 text-emerald-700',
    'PENDING': 'bg-amber-100 text-amber-700',
    'DISCHARGED': 'bg-slate-100 text-slate-600',
    'ON_HOLD': 'bg-red-100 text-red-700',
  };
  return colors[status] || 'bg-slate-100 text-slate-600';
}

function getLienStatusBadge(status) {
  const styles = {
    'SIGNED': { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle },
    'PENDING': { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock },
    'REJECTED': { bg: 'bg-red-100', text: 'text-red-700', icon: X },
    'NOT_SENT': { bg: 'bg-slate-100', text: 'text-slate-600', icon: FileText },
  };
  const style = styles[status] || styles['NOT_SENT'];
  const Icon = style.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}>
      <Icon size={14} />
      {status || 'Not Sent'}
    </span>
  );
}

export default function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const fetchPatient = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/portal/patients/${id}`);
      setPatient(res.data);
      setEditForm(res.data);
    } catch (err) {
      console.error("Failed to fetch patient:", err);
      setError("Failed to load patient details");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPatient();
  }, [fetchPatient]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/portal/patients/${id}`, editForm);
      setPatient(editForm);
      setIsEditing(false);
      toast.success("Patient updated successfully");
    } catch (err) {
      console.error("Failed to save patient:", err);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditForm(patient);
    setIsEditing(false);
  };

  const handleSendLien = async () => {
    try {
      await api.post(`/portal/patients/${id}/send-lien`);
      toast.success("Lien sent for signature");
      fetchPatient();
    } catch (err) {
      console.error("Failed to send lien:", err);
      toast.error("Failed to send lien");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex">
        <PortalSidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" />
        </main>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="min-h-screen bg-slate-50 flex">
        <PortalSidebar />
        <main className="flex-1 p-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <AlertCircle size={48} className="mx-auto text-red-400 mb-4" />
              <p className="text-red-700 font-medium">{error || "Patient not found"}</p>
              <Link to="/portal/patients" className="text-red-600 hover:underline mt-2 inline-block">
                Back to patients
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <PortalSidebar />

      <main className="flex-1 p-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Link
                to="/portal/patients"
                className="p-2 rounded-lg hover:bg-slate-200 transition-colors"
                data-testid="back-to-patients"
              >
                <ArrowLeft size={20} className="text-slate-600" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" data-testid="patient-name">
                  {patient.first_name} {patient.last_name}
                </h1>
                <p className="text-slate-500">
                  Case #{patient.case_number || 'N/A'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                    data-testid="save-patient"
                  >
                    <Save size={16} />
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 text-sm font-medium"
                  data-testid="edit-patient"
                >
                  <Edit2 size={16} />
                  Edit
                </button>
              )}
            </div>
          </div>

          {/* Status Bar */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div>
                <span className="text-xs text-slate-500 block mb-1">Status</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(patient.status)}`}>
                  {patient.status || 'Unknown'}
                </span>
              </div>
              <div>
                <span className="text-xs text-slate-500 block mb-1">Lien Status</span>
                {getLienStatusBadge(patient.lien_status)}
              </div>
              <div>
                <span className="text-xs text-slate-500 block mb-1">Total Balance</span>
                <span className="text-lg font-bold text-slate-900">
                  ${(patient.total_balance || 0).toLocaleString()}
                </span>
              </div>
            </div>
            {patient.lien_status !== 'SIGNED' && (
              <button
                onClick={handleSendLien}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                data-testid="send-lien-btn"
              >
                <FileText size={16} />
                Send Lien for Signature
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Patient Information */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                <User size={18} className="text-emerald-600" />
                <h2 className="font-semibold text-slate-900">Patient Information</h2>
              </div>
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">First Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.first_name || ''}
                        onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                        className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="font-medium text-slate-900">{patient.first_name}</p>
                    )}
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Last Name</label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editForm.last_name || ''}
                        onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                        className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm"
                      />
                    ) : (
                      <p className="font-medium text-slate-900">{patient.last_name}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Date of Birth</label>
                  {isEditing ? (
                    <input
                      type="date"
                      value={editForm.dob || ''}
                      onChange={(e) => setEditForm({...editForm, dob: e.target.value})}
                      className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{patient.dob || '—'}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Insurance</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.insurance || ''}
                      onChange={(e) => setEditForm({...editForm, insurance: e.target.value})}
                      className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{patient.insurance || '—'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Case Information */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                <Briefcase size={18} className="text-blue-600" />
                <h2 className="font-semibold text-slate-900">Case Information</h2>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Case Number</label>
                  <p className="font-medium text-slate-900 font-mono">{patient.case_number || '—'}</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Attorney</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.attorney || ''}
                      onChange={(e) => setEditForm({...editForm, attorney: e.target.value})}
                      className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{patient.attorney || '—'}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Status</label>
                  {isEditing ? (
                    <select
                      value={editForm.status || ''}
                      onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                      className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm"
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="PENDING">Pending</option>
                      <option value="DISCHARGED">Discharged</option>
                      <option value="ON_HOLD">On Hold</option>
                    </select>
                  ) : (
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(patient.status)}`}>
                      {patient.status || 'Unknown'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Treatment Information */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                <Stethoscope size={18} className="text-purple-600" />
                <h2 className="font-semibold text-slate-900">Treatment Information</h2>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Treatment Type</label>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editForm.treatment_type || ''}
                      onChange={(e) => setEditForm({...editForm, treatment_type: e.target.value})}
                      className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm"
                    />
                  ) : (
                    <p className="font-medium text-slate-900">{patient.treatment_type || '—'}</p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Treating Clinic</label>
                  <p className="font-medium text-slate-900">{patient.treating_clinic || '—'}</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Prescribed</label>
                    <p className="font-medium text-slate-900">{patient.total_visits_prescribed || 0} visits</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Completed</label>
                    <p className="font-medium text-emerald-600">{patient.visits_completed || 0} visits</p>
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Remaining</label>
                    <p className="font-medium text-amber-600">{patient.visits_remaining || 0} visits</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Initial Visit</label>
                  <p className="font-medium text-slate-900">{patient.initial_visit || '—'}</p>
                </div>
              </div>
            </div>

            {/* Financial Information */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                <DollarSign size={18} className="text-green-600" />
                <h2 className="font-semibold text-slate-900">Financial Information</h2>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Total Balance</label>
                  {isEditing ? (
                    <input
                      type="number"
                      step="0.01"
                      value={editForm.total_balance || ''}
                      onChange={(e) => setEditForm({...editForm, total_balance: parseFloat(e.target.value)})}
                      className="w-full h-9 px-3 border border-slate-200 rounded-lg text-sm"
                    />
                  ) : (
                    <p className="text-2xl font-bold text-slate-900">
                      ${(patient.total_balance || 0).toLocaleString()}
                    </p>
                  )}
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Lien Status</label>
                  {getLienStatusBadge(patient.lien_status)}
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Dates of Service</label>
                  <p className="font-medium text-slate-900">{patient.dates_of_service || '—'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <div className="mt-6 bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-200 flex items-center gap-3">
              <ClipboardList size={18} className="text-slate-600" />
              <h2 className="font-semibold text-slate-900">Notes</h2>
            </div>
            <div className="p-4">
              {isEditing ? (
                <textarea
                  value={editForm.notes || ''}
                  onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                  className="w-full h-32 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none"
                  placeholder="Add notes about this patient..."
                />
              ) : (
                <p className="text-slate-600 whitespace-pre-wrap">
                  {patient.notes || 'No notes available.'}
                </p>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
